import { useState, useEffect } from "react";
import { ChevronLeft, ChevronRight } from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import { useSettings } from "@/context/SettingsContext";
import { resolveImage } from "@/lib/api";

const HeroCarousel = () => {
  const { settings } = useSettings();
  const slides = settings?.hero_slides || [];
  const [idx, setIdx] = useState(0);

  useEffect(() => {
    if (!slides.length) return;
    const t = setInterval(() => setIdx((i) => (i + 1) % slides.length), 7000);
    return () => clearInterval(t);
  }, [slides.length]);

  if (!slides.length) return null;
  const s = slides[idx];

  const next = () => setIdx((i) => (i + 1) % slides.length);
  const prev = () => setIdx((i) => (i - 1 + slides.length) % slides.length);

  return (
    <section className="relative h-[78vh] min-h-[560px] overflow-hidden bg-primary" data-testid="hero-carousel">
      <AnimatePresence mode="sync">
        <motion.div
          key={idx}
          initial={{ opacity: 0, scale: 1.08 }}
          animate={{ opacity: 1, scale: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 1.4, ease: "easeOut" }}
          className="absolute inset-0"
        >
          <img src={resolveImage(s.image)} alt={s.title} className="w-full h-full object-cover" />
          <div className="absolute inset-0 bg-gradient-to-r from-black/80 via-black/50 to-black/20" />
        </motion.div>
      </AnimatePresence>

      <div className="relative h-full container-pad flex items-end pb-20 md:pb-24">
        <motion.div
          key={`text-${idx}`}
          initial={{ opacity: 0, y: 40 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.3 }}
          className="max-w-3xl text-white"
        >
          <div className="text-xs tracking-[0.3em] text-accent mb-5 font-medium">{s.kicker}</div>
          <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl font-light leading-[1.05] mb-6 text-balance">
            {s.title}
          </h1>
          <p className="text-base md:text-lg text-white/80 leading-relaxed max-w-2xl mb-8">{s.description}</p>
          {s.cta_text && s.cta_link && (
            <a
              href={s.cta_link}
              target={s.cta_link.startsWith("http") ? "_blank" : undefined}
              rel="noreferrer"
              className="inline-flex items-center gap-2 px-7 py-3.5 rounded-full bg-accent text-accent-foreground font-medium hover:bg-accent/90 transition-all hover:-translate-y-0.5 shadow-lg"
              data-testid={`hero-cta-${idx}`}
            >
              {s.cta_text}
              <ChevronRight className="w-4 h-4" />
            </a>
          )}
        </motion.div>
      </div>

      <div className="absolute bottom-8 right-8 flex items-center gap-3 z-10">
        <button onClick={prev} className="w-11 h-11 rounded-full border border-white/30 backdrop-blur-md text-white hover:bg-white hover:text-primary transition flex items-center justify-center" data-testid="hero-prev">
          <ChevronLeft className="w-5 h-5" />
        </button>
        <button onClick={next} className="w-11 h-11 rounded-full border border-white/30 backdrop-blur-md text-white hover:bg-white hover:text-primary transition flex items-center justify-center" data-testid="hero-next">
          <ChevronRight className="w-5 h-5" />
        </button>
      </div>

      <div className="absolute bottom-10 left-1/2 -translate-x-1/2 md:left-auto md:translate-x-0 md:right-32 flex gap-2">
        {slides.map((_, i) => (
          <button
            key={i}
            onClick={() => setIdx(i)}
            className={`h-1 rounded-full transition-all ${i === idx ? "w-10 bg-accent" : "w-5 bg-white/40"}`}
            data-testid={`hero-dot-${i}`}
            aria-label={`slide ${i + 1}`}
          />
        ))}
      </div>
    </section>
  );
};

export default HeroCarousel;
